'use client';

import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, File, Image, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { cn } from '../theme';
import { Progress } from '../Progress';
import type { FileUploadProps, FileUploadFile } from './FileUpload.types';

const generateId = () => Math.random().toString(36).substring(2, 9);

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) return Image;
  if (type.includes('pdf') || type.includes('document')) return FileText;
  return File;
};

export const FileUpload = React.forwardRef<HTMLDivElement, FileUploadProps>(
  (props, ref) => {
    const {
      label,
      helperText,
      error,
      disabled,
      className,
      accept,
      maxFiles = 10,
      maxSize = 10 * 1024 * 1024, // 10MB default
      multiple = true,
      value = [],
      onChange,
      onUpload,
      showPreview = true,
    } = props;

    const acceptConfig = React.useMemo(() => {
      if (!accept) return undefined;
      if (typeof accept === 'string') {
        return { [accept]: [] };
      }
      return accept.reduce((acc, type) => ({ ...acc, [type]: [] }), {});
    }, [accept]);

    const onDrop = React.useCallback(
      async (acceptedFiles: File[]) => {
        const remainingSlots = maxFiles - value.length;
        const filesToAdd = acceptedFiles.slice(0, remainingSlots);

        const newFiles: FileUploadFile[] = filesToAdd.map((file) => ({
          file,
          id: generateId(),
          progress: 0,
          status: 'pending' as const,
          preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
        }));

        const updatedFiles = [...value, ...newFiles];
        onChange?.(updatedFiles);

        // Auto-upload if handler provided
        if (onUpload) {
          for (const fileItem of newFiles) {
            try {
              onChange?.(
                updatedFiles.map((f) =>
                  f.id === fileItem.id ? { ...f, status: 'uploading' as const, progress: 0 } : f
                )
              );

              await onUpload(fileItem.file);

              onChange?.((prev) =>
                prev.map((f) =>
                  f.id === fileItem.id ? { ...f, status: 'success' as const, progress: 100 } : f
                )
              );
            } catch (err) {
              onChange?.((prev) =>
                prev.map((f) =>
                  f.id === fileItem.id
                    ? { ...f, status: 'error' as const, error: 'Upload failed' }
                    : f
                )
              );
            }
          }
        }
      },
      [value, maxFiles, onChange, onUpload]
    );

    const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
      onDrop,
      accept: acceptConfig,
      maxSize,
      maxFiles: maxFiles - value.length,
      multiple,
      disabled: disabled || value.length >= maxFiles,
    });

    const removeFile = (id: string) => {
      const file = value.find((f) => f.id === id);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      onChange?.(value.filter((f) => f.id !== id));
    };

    return (
      <div ref={ref} className={cn('w-full', className)}>
        {label && (
          <label className="block text-sm font-medium text-navy mb-1.5">{label}</label>
        )}

        <div
          {...getRootProps()}
          className={cn(
            'relative rounded-lg border-2 border-dashed p-6',
            'transition-colors duration-200 cursor-pointer',
            'flex flex-col items-center justify-center text-center',
            isDragActive
              ? 'border-sage bg-sage/5'
              : 'border-warm-gray/30 hover:border-sage-light hover:bg-cream-dark/50',
            (disabled || value.length >= maxFiles) && 'cursor-not-allowed opacity-50',
            error && 'border-red-500'
          )}
        >
          <input {...getInputProps()} />

          <div className="w-12 h-12 rounded-full bg-sage/10 flex items-center justify-center mb-3">
            <Upload className="w-6 h-6 text-sage" />
          </div>

          {isDragActive ? (
            <p className="text-sm text-sage font-medium">Drop files here...</p>
          ) : (
            <>
              <p className="text-sm text-navy font-medium mb-1">
                Drag & drop files here, or click to browse
              </p>
              <p className="text-xs text-warm-gray">
                {accept
                  ? `Accepted: ${Array.isArray(accept) ? accept.join(', ') : accept}`
                  : 'Any file type'}
                {' · '}Max {formatFileSize(maxSize)} per file
                {multiple && ` · Up to ${maxFiles} files`}
              </p>
            </>
          )}
        </div>

        {fileRejections.length > 0 && (
          <div className="mt-2 p-2 bg-red-50 rounded-md">
            {fileRejections.map(({ file, errors }) => (
              <p key={file.name} className="text-xs text-red-600">
                {file.name}: {errors.map((e) => e.message).join(', ')}
              </p>
            ))}
          </div>
        )}

        {value.length > 0 && (
          <ul className="mt-4 space-y-2">
            {value.map((fileItem) => {
              const FileIcon = getFileIcon(fileItem.file.type);
              return (
                <li
                  key={fileItem.id}
                  className="flex items-center gap-3 p-3 bg-cream rounded-lg"
                >
                  {showPreview && fileItem.preview ? (
                    <img
                      src={fileItem.preview}
                      alt={fileItem.file.name}
                      className="w-10 h-10 object-cover rounded"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded bg-cream-dark flex items-center justify-center">
                      <FileIcon className="w-5 h-5 text-warm-gray" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy truncate">
                      {fileItem.file.name}
                    </p>
                    <p className="text-xs text-warm-gray">
                      {formatFileSize(fileItem.file.size)}
                    </p>
                    {fileItem.status === 'uploading' && (
                      <Progress value={fileItem.progress} size="sm" className="mt-1" />
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {fileItem.status === 'success' && (
                      <CheckCircle className="w-5 h-5 text-success" />
                    )}
                    {fileItem.status === 'error' && (
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(fileItem.id);
                      }}
                      className="p-1 rounded hover:bg-cream-dark transition-colors"
                      disabled={disabled}
                    >
                      <X className="w-4 h-4 text-warm-gray" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {helperText && !error && (
          <p className="mt-1.5 text-sm text-warm-gray">{helperText}</p>
        )}
        {error && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
      </div>
    );
  }
);

FileUpload.displayName = 'FileUpload';

export default FileUpload;
